import { Injectable, inject } from '@angular/core';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { CowWithMeasurement } from '../../../models/cow.model';
import { GraphDataDto } from '../../../models/measurement.model';
import { CowService } from '../../../services/cow.service';
import { toDateOnlyString } from '../../../utils/date.utils';

@Injectable()
export class MeasurementsGraphDataFacade {
  private readonly cowService = inject(CowService);

  public loadCompanyGraphData(selectedDates: Date[]): Observable<GraphDataDto> {
    return this.cowService.getChartData(selectedDates);
  }

  public loadSingleCowGraphData(cowId: string, availableDates: Date[]): Observable<GraphDataDto> {
    if (availableDates.length === 0) {
      return of(this.createEmptyGraphData());
    }

    const referenceDate = availableDates[0];

    return forkJoin({
      referenceData: this.cowService
        .getChartData([referenceDate])
        .pipe(catchError(() => of(this.createEmptyGraphData()))),
      allMeasurements: this.cowService
        .getCowMeasurementHistory(cowId, false)
        .pipe(catchError(() => of([] as CowWithMeasurement[]))),
    }).pipe(
      map(({ referenceData, allMeasurements }) => {
        const measurements = allMeasurements
          .filter((cow) => !!cow.heightCm)
          .sort((a, b) => a.ageInMonths - b.ageInMonths);

        return {
          dataByDate: [
            {
              measurementDate: toDateOnlyString(referenceDate),
              measurements,
              trendLine: measurements.map((cow) => ({ x: cow.ageInMonths, y: cow.heightCm })),
            },
          ],
          lowerGrowthCurve: referenceData.lowerGrowthCurve,
          averageGrowthCurve: referenceData.averageGrowthCurve,
          upperGrowthCurve: referenceData.upperGrowthCurve,
        } satisfies GraphDataDto;
      }),
    );
  }

  public createEmptyGraphData(): GraphDataDto {
    return {
      dataByDate: [],
      lowerGrowthCurve: [],
      averageGrowthCurve: [],
      upperGrowthCurve: [],
    };
  }
}
